import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, OnInit, TemplateRef, computed, inject, signal, viewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AlertService,
  AlertType,
  ButtonConfig,
  ButtonType,
  CheckboxFieldConfig,
  ColumnConfig,
  ConfirmationDialogService,
  CustomAction,
  FieldType,
  GenericButtonComponent,
  GenericModalComponent,
  GenericTableComponent,
  HeaderService,
  LoaderService,
  ModalFieldConfig,
  ModalMode,
  TimeFieldConfig,
  TimeInterval,
} from '@solcre-org/core-ui';
import { finalize } from 'rxjs';
import { PortOvertimeLevel } from '../../../shared/enums/port-overtime-level.enum';
import { PortOvertimeRuleModel } from '../../../shared/models/port-overtime-rule.model';
import { PortOvertimeRuleService } from '../../../shared/services/port-overtime-rule.service';
import { PortOvertimeRuleFormModel } from './port-overtime-rule-form.model';
import { PortOvertimeRuleFormService } from './port-overtime-rule-form.service';
import { OvertimeClock } from './overtime-clock/overtime-clock';

type PortOvertimeRulesContext = {
  portCode: string;
  portName: string;
  terminal: string;
  country: string;
  berthingType: string;
  timezone: string;
};

type ViewMode = 'clock' | 'list';

@Component({
  selector: 'app-port-overtime-rules',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, GenericModalComponent, GenericTableComponent, GenericButtonComponent, OvertimeClock],
  templateUrl: './port-overtime-rules.html',
  styleUrls: ['./port-overtime-rules.css'],
})
export class PortOvertimeRules implements OnInit, OnDestroy {
  readonly Level = PortOvertimeLevel;

  viewMode = signal<ViewMode>('clock');

  levelCellTemplate = viewChild<TemplateRef<{ $implicit: PortOvertimeRuleModel; column: ColumnConfig<PortOvertimeRuleModel> }>>('levelCellTemplate');

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly headerService = inject(HeaderService);
  private readonly loaderService = inject(LoaderService);
  private readonly alertService = inject(AlertService);
  private readonly confirmService = inject(ConfirmationDialogService);
  private readonly translateService = inject(TranslateService);
  private readonly ruleService = inject(PortOvertimeRuleService);
  private readonly formService = inject(PortOvertimeRuleFormService);

  portId = signal<string>('');
  context = signal<PortOvertimeRulesContext>({
    portCode: '',
    portName: '',
    terminal: '',
    country: '',
    berthingType: '',
    timezone: '',
  });
  isLoading = signal(false);
  rules = signal<PortOvertimeRuleModel[]>([]);

  ruleFormOpen = signal(false);
  ruleModalMode = signal<ModalMode>(ModalMode.CREATE);
  editingRuleId = signal<string | null>(null);
  // Plain property — GenericModalComponent clones the data on open; mutations here
  // after that have no effect on the live form.
  ruleDraft: PortOvertimeRuleFormModel = this.formService.createEmptyDraft();

  hasPortContext = computed(() => !!this.context().country && !!this.context().timezone);

  // Shared with core-generic-table's [listTitle]/[listSubTitle] too — GenericTableComponent
  // pushes those straight into HeaderService on init (and blanks them on destroy), so the
  // list view must feed it the same text or the header goes blank while it's showing.
  //
  // Plain methods, not computed() — computed() only re-evaluates when a *signal* it reads
  // changes, and translateService.instant() isn't a signal. If this were memoized, calling
  // updateHeaderConfig() again once translations load (see the onLangChange/onTranslationChange
  // subscriptions in ngOnInit) would just return the stale, untranslated cached value.
  headerTitleText(): string {
    const context = this.context();
    const portName = context.portName || context.portCode || this.t('portOvertimeRules.header.fallbackTitle');
    return this.t('portOvertimeRules.header.title', { portName });
  }

  headerSubtitleText(): string {
    const context = this.context();
    const metaParts = [context.portCode, context.country, context.terminal].filter(Boolean);
    return metaParts.join(' · ') || this.t('portOvertimeRules.header.fallbackMeta');
  }

  levelFieldConfig = computed<ModalFieldConfig<PortOvertimeRuleFormModel>>(() => ({
    key: 'level',
    label: this.t('portOvertimeRules.fields.level'),
    type: FieldType.SELECT,
    readonly: false,
    options: this.formService.levelOptions(),
    validators: [],
  }));

  daysFieldConfig = computed<ModalFieldConfig<PortOvertimeRuleFormModel> & { checkboxConfig: CheckboxFieldConfig }>(() => ({
    key: 'daysOfWeek',
    label: this.t('portOvertimeRules.fields.daysOfWeek'),
    type: FieldType.CHECKBOX,
    readonly: false,
    checkboxConfig: {
      options: this.formService.dayOptions(),
      allowMultiple: true,
      separatedOptions: true,
    },
    validators: [],
  }));

  timeFieldConfig = computed<ModalFieldConfig<PortOvertimeRuleFormModel> & { config: TimeFieldConfig }>(() => ({
    key: 'time',
    label: this.t('portOvertimeRules.fields.time'),
    type: FieldType.TIME,
    readonly: false,
    config: {
      // Shared by both the start and end pickers (core-ui doesn't support separate
      // lists) — 00:00 through 24:00, so a full day (00:00 start, 24:00 end) is reachable.
      options: this.formService.timeOptions(),
      timeInterval: TimeInterval.MINUTES_30,
      includeEndTime: true,
      startTimeLabel: this.t('portOvertimeRules.fields.startTime'),
      endTimeLabel: this.t('portOvertimeRules.fields.endTime'),
      // The backend treats endTime <= startTime as "crosses midnight" — the field must not
      // block that combination, or a Fri 19:00–07:00 window could never be entered.
      enforceEndTimeAfterStart: false,
    },
    validators: [],
  }));

  ruleModalFields = computed<ModalFieldConfig<PortOvertimeRuleFormModel>[]>(() => [
    this.levelFieldConfig(),
    this.daysFieldConfig(),
    this.timeFieldConfig(),
  ]);

  // Same core-ui icon-button chrome as any row action (ButtonType.ICON) — no bespoke
  // CSS box model, just the app's existing button component so spacing matches everywhere.
  clockViewButtonConfig = computed<ButtonConfig>(() => ({
    type: ButtonType.ICON,
    icon: 'icon-clock',
    ariaLabel: this.t('portOvertimeRules.viewMode.clock'),
    tooltip: this.t('portOvertimeRules.viewMode.clock'),
    tooltipConfig: { hasTooltip: true, text: this.t('portOvertimeRules.viewMode.clock'), position: 'bottom' },
    customClass: this.viewMode() === 'clock' ? 'is-active' : undefined,
  }));

  listViewButtonConfig = computed<ButtonConfig>(() => ({
    type: ButtonType.ICON,
    icon: 'icon-list',
    ariaLabel: this.t('portOvertimeRules.viewMode.list'),
    tooltip: this.t('portOvertimeRules.viewMode.list'),
    tooltipConfig: { hasTooltip: true, text: this.t('portOvertimeRules.viewMode.list'), position: 'bottom' },
    customClass: this.viewMode() === 'list' ? 'is-active' : undefined,
  }));

  ruleColumns = computed<ColumnConfig<PortOvertimeRuleModel>[]>(() => [
    {
      label: this.t('portOvertimeRules.fields.level'),
      key: 'level',
      template: this.levelCellTemplate(),
    },
    {
      label: this.t('portOvertimeRules.fields.daysOfWeek'),
      key: 'daysOfWeek',
      format: (_value, row) => this.daysLabel(row),
    },
    {
      label: this.t('portOvertimeRules.fields.time'),
      key: 'startTime',
      format: (_value, row) => this.scheduleLabel(row),
    },
  ]);

  ruleCustomActions = computed<CustomAction<PortOvertimeRuleModel>[]>(() => [
    {
      title: this.t('portOvertimeRules.actions.editRule'),
      icon: 'icon-edit',
      buttonConfig: {
        type: ButtonType.ICON,
        icon: 'icon-edit',
      },
      isExtra: false,
      tooltipPosition: 'top',
      callback: (rule) => this.openEditRule(rule),
    },
    {
      title: this.t('portOvertimeRules.actions.deleteRule'),
      icon: 'icon-delete',
      buttonConfig: {
        type: ButtonType.ICON,
        icon: 'icon-delete',
      },
      class: 'context:error',
      isExtra: false,
      tooltipPosition: 'top',
      callback: (rule) => this.deleteRule(rule),
    },
  ]);

  sortedRules = computed(() => {
    return [...this.rules()].sort((left, right) => {
      const leftDay = Math.min(...(left.daysOfWeek.length ? left.daysOfWeek : [8]));
      const rightDay = Math.min(...(right.daysOfWeek.length ? right.daysOfWeek : [8]));
      if (leftDay !== rightDay) {
        return leftDay - rightDay;
      }
      return String(left.startTime ?? '').localeCompare(String(right.startTime ?? ''));
    });
  });

  ngOnInit(): void {
    // The header renders its title as plain text, not through the `translate` pipe, so an
    // instant() call made before the translation file finishes loading (e.g. a fresh deep
    // link) would get stuck showing the raw key forever. Re-run the header config whenever
    // translations (re)load so it always ends up showing the resolved text.
    this.translateService.onTranslationChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.updateHeaderConfig());
    this.translateService.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.updateHeaderConfig());

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.portId.set(params.get('id') ?? '');
      this.reloadData();
    });

    // Display metadata (portName, country, timezone…) arrives as query params — no extra GET.
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.context.set({
        portCode: params.get('portCode') ?? '',
        portName: params.get('portName') ?? '',
        terminal: params.get('terminal') ?? '',
        country: params.get('country') ?? '',
        berthingType: params.get('berthingType') ?? '',
        timezone: params.get('timezone') ?? '',
      });
      this.updateHeaderConfig();
      this.reloadData();
    });
  }

  reloadData(): void {
    if (!this.portId() || !this.hasPortContext()) {
      this.rules.set([]);
      return;
    }

    this.isLoading.set(true);
    this.loaderService.showLoader();
    this.ruleService
      .fetchByPort(this.portId())
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
          this.loaderService.hideLoader();
        })
      )
      .subscribe({
        next: (rules) => this.rules.set(rules),
        error: (error: HttpErrorResponse) => this.showError(error),
      });
  }

  goBackToPorts(): void {
    this.router.navigate(['/app/ports']);
  }

  dayLabel(day: number): string {
    return this.formService.dayLabel(day);
  }

  levelLabel(level: PortOvertimeLevel | string | undefined): string {
    return level === this.Level.OT1 ? this.t('portOvertimeRules.levels.ot1') : this.t('portOvertimeRules.levels.ot2');
  }

  daysLabel(rule: PortOvertimeRuleModel): string {
    return [...rule.daysOfWeek]
      .sort((left, right) => left - right)
      .map((day) => this.dayLabel(day))
      .join(', ');
  }

  scheduleLabel(rule: PortOvertimeRuleModel): string {
    const range = `${rule.startTime}–${rule.endTime}`;
    return rule.crossesMidnight() ? `${range} (${this.t('portOvertimeRules.crossesMidnight')})` : range;
  }

  setViewMode(mode: ViewMode): void {
    this.viewMode.set(mode);
    // core-generic-table pushes its own [listTitle]/[listSubTitle] into HeaderService on
    // init and blanks it via clearHeaderData() on destroy — both happen on the change
    // detection cycle triggered by the signal write above, after this call returns. Restore
    // our header once that's settled.
    setTimeout(() => this.updateHeaderConfig());
  }

  openCreateRule(): void {
    this.editingRuleId.set(null);
    this.ruleDraft = this.formService.createEmptyDraft();
    this.ruleModalMode.set(ModalMode.CREATE);
    this.ruleFormOpen.set(true);
  }

  openRuleById(ruleId: string): void {
    const rule = this.rules().find((item) => String(item.id) === ruleId);
    if (rule) {
      this.openEditRule(rule);
    }
  }

  openEditRule(rule: PortOvertimeRuleModel): void {
    this.editingRuleId.set(String(rule.id ?? ''));
    this.ruleDraft = this.formService.draftFromModel(rule);
    this.ruleModalMode.set(ModalMode.EDIT);
    this.ruleFormOpen.set(true);
  }

  closeRuleForm(): void {
    this.ruleFormOpen.set(false);
  }

  onRuleModalSave(formValue: PortOvertimeRuleFormModel): void {
    const validationError = this.formService.validate(formValue);
    if (validationError) {
      this.showMessage(validationError, AlertType.DEFAULT);
      return;
    }

    const payload = this.formService.buildPayload(this.portId(), formValue);
    const editingId = this.editingRuleId();
    const request = editingId ? this.ruleService.update(editingId, payload) : this.ruleService.create(payload);

    this.loaderService.showLoader();
    request.pipe(finalize(() => this.loaderService.hideLoader())).subscribe({
      next: () => {
        this.showMessage(
          editingId ? this.t('portOvertimeRules.messages.ruleUpdated') : this.t('portOvertimeRules.messages.ruleCreated'),
          AlertType.SUCCESS
        );
        this.closeRuleForm();
        this.reloadData();
      },
      // Keep the modal open with the user's data on a 422 overlap rejection (or any other error)
      // instead of closing/resetting the form.
      error: (error: HttpErrorResponse) => this.showError(error),
    });
  }

  deleteRule(rule: PortOvertimeRuleModel): void {
    if (!rule?.id) {
      return;
    }

    this.confirmService
      .openConfirm({
        title: this.t('portOvertimeRules.confirm.deleteTitle'),
        message: this.t('portOvertimeRules.confirm.deleteMessage'),
      })
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }

        this.loaderService.showLoader();
        this.ruleService
          .remove(String(rule.id))
          .pipe(finalize(() => this.loaderService.hideLoader()))
          .subscribe({
            next: () => {
              this.showMessage(this.t('portOvertimeRules.messages.ruleDeleted'), AlertType.SUCCESS);
              this.reloadData();
            },
            error: (error: HttpErrorResponse) => this.showError(error),
          });
      });
  }

  ngOnDestroy(): void {
    this.headerService.clearCustomActions();
    this.headerService.clearHeaderData();
  }

  private updateHeaderConfig(): void {
    this.headerService.setHeaderConfig({
      title: this.headerTitleText(),
      text: this.headerSubtitleText(),
      visible: true,
      showDefaultFilter: false,
      showDefaultCreate: false,
      customActions: [],
    });
  }

  private showMessage(message: string, type: AlertType): void {
    this.alertService.show({
      type,
      message,
      duration: 2500,
      inBottom: true,
    });
  }

  private showError(error: HttpErrorResponse): void {
    const payload = error?.error;
    const detail =
      payload?.detail ??
      payload?.error ??
      payload?.message ??
      (Array.isArray(payload?.violations)
        ? payload.violations.map((violation: any) => violation?.message).filter(Boolean).join(' · ')
        : null) ??
      this.t('errors.generic');

    this.showMessage(detail, AlertType.ERROR);
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translateService.instant(key, params);
  }
}
